import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { capitalize, get, isEmpty, map } from 'lodash';
import cx from 'classnames';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import moment from 'moment';

import { Layout, Main } from '@nextiva/next-ui';

import { SideNav } from '../SideNav';
import { Network } from '../Network';
import { Header } from '../Header';
import { Scale } from '../Scale';
import { Footer } from '../Footer';
import { Spinner } from '../Spinner';
import { Table } from '../Table';
import styles from './App.module.scss';
import { EmptyPlaceholder } from '../EmptyPlaceholder';

export class App extends Component {
  static getItemTitle(item) {
    return get(
      item,
      item.type === 'ticket' ? 'attributes.subject' : 'attributes.name',
    );
  }

  static formatCoverage(value) {
    return Math.floor(value.toFixed(2) * 100);
  }

  getScaleData() {
    if (!this.props.entityData) return [];

    const timeplotData = this.props.entityData.timeseries_plot.data;

    return naturalOrderBy(
      map(timeplotData, (value, date) => ({
        x: moment(date).format('D-MMM-YY'),
        y: value,
      })),
      [data => data.x],
      ['asc'],
    );
  }

  shouldShowEmptyPlaceholder() {
    return (
      Boolean(this.props.selectedItem) &&
      isEmpty(get(this.props.entityData, 'graph.nodes')) &&
      isEmpty(get(this.props.entityData, 'graph.edges'))
    );
  }

  render() {
    let type;
    let title;

    const { entityData, selectedItem } = this.props;

    if (selectedItem) {
      type = capitalize(selectedItem.type);
      title = App.getItemTitle(selectedItem);
    }

    return (
      <Layout>
        <Header />
        <SideNav
          tickets={this.props.tickets}
          sales={this.props.sales}
          handleItemClick={this.props.handleItemClick}
          selectedItem={selectedItem}
          fetchAllSales={this.props.fetchAllSales}
          fetchAllTickets={this.props.fetchAllTickets}
          fetchSalesById={this.props.fetchSalesById}
          fetchTicketsById={this.props.fetchTicketsById}
        />
        <Main>
          <div className={styles.wrap}>
            {this.props.isLoading && (
              <Spinner size={150} thickness={10} className={styles.spinner} />
            )}
            {!this.props.isLoading && (
              <React.Fragment>
                <h1 className={styles.header}>
                  {selectedItem ? `${type} [${title}] ` : 'Employee '}
                  Communication Insights
                </h1>
                {!selectedItem && (
                  <h3 className={styles.note}>
                    Please select a ticket/sale in sidebar
                  </h3>
                )}
                <div
                  className={cx(styles.graphs, {
                    [styles.graphsDisabled]: !selectedItem,
                  })}
                >
                  <Network
                    isItemSelected={Boolean(selectedItem)}
                    noData={this.shouldShowEmptyPlaceholder()}
                    graphData={entityData && entityData.graph}
                  />
                  <div>
                    <div className={styles.scaleWrapper}>
                      {this.shouldShowEmptyPlaceholder() ? (
                        <EmptyPlaceholder />
                      ) : (
                        <Scale data={this.getScaleData()} />
                      )}
                    </div>
                    {entityData && entityData.graph.comm_coverage_ratio && (
                      <div className={styles.communicationCoverage}>
                        Communication coverage:{' '}
                        <span className={styles.chatIcon}>💬</span>{' '}
                        <span className={styles.communicationCoverageValue}>
                          {App.formatCoverage(
                            entityData.graph.comm_coverage_ratio,
                          )}
                          %
                        </span>
                      </div>
                    )}
                    {!this.shouldShowEmptyPlaceholder() &&
                      (this.props.selectedItem && (
                        <Table
                          inboundCount={get(entityData, 'graph.inbound_count')}
                          outboundCount={get(
                            entityData,
                            'graph.outbound_count',
                          )}
                          totalCount={get(entityData, 'graph.total_count')}
                          nodes={get(entityData, 'graph.nodes')}
                        />
                      ))}
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
          <Footer />
        </Main>
      </Layout>
    );
  }
}

App.propTypes = {
  isLoading: PropTypes.bool,
  sales: PropTypes.object,
  tickets: PropTypes.object,
  entityData: PropTypes.object,
  selectedItem: PropTypes.object,
  handleItemClick: PropTypes.func.isRequired,
  fetchAllSales: PropTypes.func.isRequired,
  fetchAllTickets: PropTypes.func.isRequired,
  fetchTicketsById: PropTypes.func.isRequired,
  fetchSalesById: PropTypes.func.isRequired,
};
